// Operator Reconciliation tab (Neel feedback #7).
//
// Operators reach reconciliation by connecting a bank account via open banking.
// Until CoLiviq reviews + approves the request (and the finAPI third-party
// hookup lands — license pending), this surface walks them through:
//   no connection  → "+ Connect bank account" → a review request
//   pending review  → "Open banking for •••• {last4} is under review"
//   approved        → connected (live reconciliation is set up server-side)
// The submission is scoped to the operator's own org by RLS + a trigger.
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOperator } from "@/hooks/useOperator";
import { useLanguage } from "@/contexts/LanguageContext";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Landmark, Clock, CheckCircle2, Plus } from "lucide-react";
import { operatorStamped } from "@/lib/operator-stamped";

interface BankSubmission {
  id: string;
  iban: string;
  status: "pending" | "approved" | "rejected";
  account_holder: string;
  review_note: string | null;
}

const last4 = (iban: string) => iban.replace(/\s+/g, "").slice(-4);

export function OperatorReconciliation() {
  const { operator } = useOperator();
  const { t } = useLanguage();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [holder, setHolder] = useState("");
  const [iban, setIban] = useState("");
  const [bic, setBic] = useState("");

  const QK = ["operator-bank-submission", operator?.id];
  const { data: active, isLoading } = useQuery({
    queryKey: QK,
    enabled: !!operator?.id,
    queryFn: async (): Promise<BankSubmission | null> => {
      // RLS returns this org's rows only; the active one is pending/approved.
      const { data, error } = await supabase
        .from("operator_bank_submissions")
        .select("id, iban, status, account_holder, review_note")
        .in("status", ["pending", "approved"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as BankSubmission) ?? null;
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      // operator_id + submitter are server-derived (trigger); we send only the
      // bank fields.
      const { error } = await supabase.from("operator_bank_submissions").insert(
        operatorStamped<"operator_bank_submissions">({
          account_holder: holder.trim(),
          iban: iban.replace(/\s+/g, ""),
          bic: bic.trim() || null,
        })
      );
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(
        "Submitted for review — we'll set up open banking shortly."
      );
      setDialogOpen(false);
      setHolder("");
      setIban("");
      setBic("");
      qc.invalidateQueries({ queryKey: QK });
    },
    onError: (e: unknown) =>
      toast.error(
        e instanceof Error ? e.message : "Could not submit — please try again."
      ),
  });

  const canSubmit =
    holder.trim().length > 1 && iban.replace(/\s+/g, "").length >= 15;

  // Title comes from AdminV2Layout (which also renders the top nav bar); the
  // body keeps the sub-description.
  const intro = (
    <p className="text-muted-foreground max-w-2xl text-sm sm:text-base">
      {t(
        "operator.reconciliation.subtitle",
        "Connect your bank account to reconcile incoming rent automatically against your tenants — no manual matching."
      )}
    </p>
  );

  const body = useMemo(() => {
    if (isLoading) return null;

    if (active?.status === "approved") {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden />
              Bank account connected
            </CardTitle>
            <CardDescription>
              Open banking for the account ending •••• {last4(active.iban)} is
              approved. Automatic reconciliation is being switched on — you'll
              see matched payments here shortly.
            </CardDescription>
          </CardHeader>
        </Card>
      );
    }

    if (active?.status === "pending") {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-5 w-5 text-amber-600" aria-hidden />
              Under review
            </CardTitle>
            <CardDescription>
              Open banking for the account ending •••• {last4(active.iban)} is
              under review. We'll email you once it's connected.
            </CardDescription>
          </CardHeader>
        </Card>
      );
    }

    // No active connection → invite them to connect.
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Landmark className="h-5 w-5 text-muted-foreground" aria-hidden />
            No bank account connected
          </CardTitle>
          <CardDescription>
            Connect a bank account to start reconciling rent. We'll review the
            details and switch on open banking for you.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Connect bank account
          </Button>
        </CardContent>
      </Card>
    );
  }, [active, isLoading]);

  return (
    <AdminV2Layout title={t("admin.nav.reconciliation", "Reconciliation")}>
      <div className="mx-auto max-w-5xl space-y-6">
        {intro}
        {body}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Connect a bank account</DialogTitle>
              <DialogDescription>
                We'll set up open banking for this account after a quick review.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="obs-holder">Account holder</Label>
                <Input
                  id="obs-holder"
                  value={holder}
                  onChange={(e) => setHolder(e.target.value)}
                  placeholder={operator?.name ?? "Your company GmbH"}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="obs-iban">IBAN</Label>
                <Input
                  id="obs-iban"
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  placeholder="DE00 0000 0000 0000 0000 00"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="obs-bic">
                  BIC <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="obs-bic"
                  value={bic}
                  onChange={(e) => setBic(e.target.value)}
                  placeholder="ABCDDEFFXXX"
                  autoComplete="off"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={() => submit.mutate()}
                disabled={!canSubmit || submit.isPending}
              >
                {submit.isPending ? "Submitting…" : "Submit for review"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminV2Layout>
  );
}
